import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with interceptor for token
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Store the getAccessTokenSilently function
let getTokenFunction = null;

export const setAuth0TokenGetter = (fn) => {
  getTokenFunction = fn;
};

// Token refresh mutex — prevents concurrent refresh attempts from racing
let refreshPromise = null;

const refreshToken = async () => {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      if (!getTokenFunction) return null;
      const token = await getTokenFunction();
      return token || null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

// Add request interceptor to include token
apiClient.interceptors.request.use(
  async (config) => {
    if (getTokenFunction) {
      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('Auth0 token is null or undefined. Request may fail with 401.');
        }
      } catch (error) {
        console.error('Failed to get Auth0 token:', error.message);
        // Continue with request - let the response interceptor handle 401
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Store logout function
let logoutFunction = null;
let isLoggingOut = false;

export const setAuth0Logout = (fn) => {
  logoutFunction = fn;
};

const forceLogout = () => {
  // Prevent multiple simultaneous logout calls
  if (isLoggingOut) return;
  isLoggingOut = true;
  if (logoutFunction) {
    logoutFunction({ logoutParams: { returnTo: window.location.origin } });
  } else {
    localStorage.clear();
    window.location.href = window.location.origin;
  }
};

// Add response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Use the mutex — all concurrent 401s share one refresh call
        const token = await refreshToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        } else {
          console.error('Token refresh returned null - authentication failed');
          forceLogout();
          return Promise.reject(new Error('Authentication failed'));
        }
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError.message);
        forceLogout();
        return Promise.reject(new Error('Authentication failed'));
      }
    }

    return Promise.reject(error);
  }
);

// Fetch all pages from a paginated DRF endpoint, returning a flat array of results.
// startUrl should be a relative path (e.g. '/devices/?site_id=1&page_size=200').
const fetchAllPages = async (startUrl) => {
  let allResults = [];
  let url = startUrl;
  while (url) {
    const response = await apiClient.get(url);
    const data = response.data;
    const results = data.results !== undefined ? data.results : data;
    allResults = allResults.concat(Array.isArray(results) ? results : []);
    if (data.next) {
      // Strip the absolute base AND /api prefix so apiClient (baseURL=/api) doesn't double it
      url = data.next.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api\//, '/');
    } else {
      url = null;
    }
  }
  return allResults;
};

const authService = {
  // Note: Login is now handled by Auth0 in the Login component
  // These methods are kept for backward compatibility but use Auth0

  // Check if user is authenticated (now handled by Auth0)
  isAuthenticated: () => {
    // This is now handled by Auth0's useAuth0 hook
    // Keeping for backward compatibility
    return true; // Auth0 handles authentication state
  },

  // Get access token (now from Auth0)
  getAccessToken: async () => {
    if (getTokenFunction) {
      try {
        return await getTokenFunction();
      } catch (error) {
        console.error('Failed to get token:', error);
        return null;
      }
    }
    return null;
  },

  // Get user's sites
  getSites: async () => {
    try {
      // NB: the token is attached by the apiClient request interceptor from
      // Auth0 (getTokenFunction), not from localStorage — don't log a
      // localStorage check here, it's always "missing" and misleads debugging.
      const response = await apiClient.get('/sites/');
      // Django REST Framework returns paginated results
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching sites:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch sites');
    }
  },

  // Get site summary with all nested data
  getSiteSummary: async (siteId) => {
    try {
      console.log('Fetching site summary for site:', siteId);
      const response = await apiClient.get(`/sites/${siteId}/summary/`);
      console.log('Site summary response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching site summary:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch site summary');
    }
  },

  // Toggle favourite status for a site
  toggleSiteFavourite: async (siteId) => {
    try {
      const response = await apiClient.post(`/sites/${siteId}/toggle-favourite/`);
      return response.data;
    } catch (error) {
      console.error('Error toggling site favourite:', error.response?.status, error.response?.data);
      throw new Error('Failed to toggle site favourite');
    }
  },

  // Get unit with devices
  getUnitWithDevices: async (unitId) => {
    try {
      const response = await apiClient.get(`/units/${unitId}/with_devices/`);
      return response.data;
    } catch (error) {
      throw new Error('Failed to fetch unit data');
    }
  },

  // Site-level hourly rollup of avg temperature/humidity. Single request
  // replaces the previous per-device sparkline fan-out.
  getSiteEnvironmentalRollup: async (siteId, hours = 24) => {
    try {
      const response = await apiClient.get(`/sites/${siteId}/environmental-rollup/?hours=${hours}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site environmental rollup:', error.response?.status);
      throw error;
    }
  },

  // Get device readings
  getDeviceReadings: async (deviceId, limit = 20) => {
    try {
      const response = await apiClient.get(`/devices/${deviceId}/readings/?limit=${limit}`);
      // Handle paginated response for readings
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error in getDeviceReadings:', error);
      // Preserve the original error so the caller can access response details
      if (error.response) {
        error.message = error.response.data?.error || error.response.data?.detail || 'Failed to fetch device readings';
      } else if (!error.message) {
        error.message = 'Failed to fetch device readings';
      }
      throw error;
    }
  },

  // Get dashboard types
  getDashTypes: async () => {
    try {
      const response = await apiClient.get('/dashtypes/');
      return response.data.results || response.data;
    } catch (error) {
      throw new Error('Failed to fetch dashboard types');
    }
  },

  // Get gateways for map
  getGateways: async () => {
    try {
      console.log('Fetching gateways from:', `${API_BASE_URL}/gateways/`);
      const response = await apiClient.get('/gateways/');
      console.log('Gateways response:', response.data);
      // Handle paginated response for gateways
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching gateways:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch gateways');
    }
  },

  // Get alerts
  getAlerts: async (status = null) => {
    try {
      const base = status ? `/alerts/?status=${status}&page_size=200` : '/alerts/?page_size=200';
      return await fetchAllPages(base);
    } catch (error) {
      console.error('Error fetching alerts:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch alerts');
    }
  },

  // Lightweight count of open + unread alerts. Used by the dashboard badge
  // poller instead of fetching the entire alerts list every 60s. Returns
  // `{ count, critical_count }` so the global-assistant tile can read its
  // CRITICAL/EMERGENCY total from the same request.
  getAlertsUnreadCount: async () => {
    try {
      const response = await apiClient.get('/alerts/unread-count/');
      return {
        count: response.data?.count ?? 0,
        critical_count: response.data?.critical_count ?? 0,
      };
    } catch (error) {
      console.error('Error fetching alert unread count:', error.response?.status);
      return { count: 0, critical_count: 0 };
    }
  },

  // Lightweight severity breakdown of OPEN alerts for the dashboard KPI.
  // Replaces fetching the entire alert history just to count by level.
  getAlertStats: async () => {
    try {
      const response = await apiClient.get('/alerts/stats/');
      return response.data;
    } catch (error) {
      console.error('Error fetching alert stats:', error.response?.status);
      return { emergency: 0, critical: 0, warning: 0, info: 0, total: 0 };
    }
  },

  // Open-alert counts grouped per site ({ "<siteId>": {total,critical,warning,info,emergency} }).
  // Replaces downloading the full alert list to compute the sites-list badges.
  getSiteAlertCounts: async () => {
    try {
      const response = await apiClient.get('/alerts/site-counts/');
      return response.data || {};
    } catch (error) {
      console.error('Error fetching site alert counts:', error.response?.status);
      return {};
    }
  },

  // Get alerts for a specific site
  getSiteAlerts: async (siteId, status = null) => {
    try {
      const params = new URLSearchParams();
      params.append('site_id', siteId);
      params.append('page_size', '200');
      if (status) params.append('status', status);
      return await fetchAllPages(`/alerts/?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching site alerts:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch site alerts');
    }
  },

  getUnitAlerts: async (unitId) => {
    try {
      return await fetchAllPages(`/alerts/?unit_id=${unitId}&page_size=200`);
    } catch (error) {
      console.error('Error fetching unit alerts:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch unit alerts');
    }
  },

  // Get devices for a specific site
  getSiteDevices: async (siteId) => {
    try {
      return await fetchAllPages(`/devices/?site_id=${siteId}&page_size=200`);
    } catch (error) {
      console.error('Error fetching site devices:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch site devices');
    }
  },

  // Get alert templates
  getAlertTemplates: async () => {
    try {
      console.log('Fetching alert templates from:', `${API_BASE_URL}/alert-templates/`);
      const response = await apiClient.get('/alert-templates/?page_size=500');
      console.log('Alert templates response:', response.data);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching alert templates:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch alert templates');
    }
  },

  // Create alert template
  createAlertTemplate: async (templateData) => {
    try {
      console.log('Creating alert template:', templateData);
      const response = await apiClient.post('/alert-templates/', templateData);
      console.log('Alert template created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating alert template:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Failed to create alert template');
    }
  },

  // Update alert template
  updateAlertTemplate: async (templateId, templateData) => {
    try {
      console.log('Updating alert template:', templateId, templateData);
      const response = await apiClient.put(`/alert-templates/${templateId}/`, templateData);
      console.log('Alert template updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating alert template:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Failed to update alert template');
    }
  },

  // Delete alert template
  deleteAlertTemplate: async (templateId) => {
    try {
      console.log('Deleting alert template:', templateId);
      await apiClient.delete(`/alert-templates/${templateId}/`);
      console.log('Alert template deleted');
    } catch (error) {
      console.error('Error deleting alert template:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete alert template');
    }
  },

  // Get alert configurations
  getAlertConfigs: async () => {
    try {
      console.log('Fetching alert configs from:', `${API_BASE_URL}/alert-configs/`);
      const response = await apiClient.get('/alert-configs/');
      console.log('Alert configs response:', response.data);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching alert configs:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch alert configs');
    }
  },

  // Create alert configuration
  createAlertConfig: async (alertConfig) => {
    try {
      console.log('Creating alert config:', alertConfig);
      const response = await apiClient.post('/alert-configs/', alertConfig);
      console.log('Alert config created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating alert config:', error.response?.status, error.response?.data);
      throw new Error('Failed to create alert configuration');
    }
  },

  // Update alert configuration
  updateAlertConfig: async (configId, alertConfig) => {
    try {
      console.log('Updating alert config:', configId, alertConfig);
      // Use PATCH for partial updates (when only status fields are being updated)
      // PUT for full updates (when all fields are being updated)
      const keys = Object.keys(alertConfig);
      // If only updating enabled and/or draft, use PATCH
      const isPartialUpdate = keys.every(key => key === 'enabled' || key === 'draft');
      const method = isPartialUpdate ? 'patch' : 'put';
      console.log('Using method:', method, 'for keys:', keys);
      const response = await apiClient[method](`/alert-configs/${configId}/`, alertConfig);
      console.log('Alert config updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating alert config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Failed to update alert configuration');
    }
  },

  // Delete alert configuration
  deleteAlertConfig: async (configId) => {
    try {
      console.log('Deleting alert config:', configId);
      const response = await apiClient.delete(`/alert-configs/${configId}/`);
      console.log('Alert config deleted');
      return response.data;
    } catch (error) {
      console.error('Error deleting alert config:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete alert configuration');
    }
  },

  // Get device types
  getDeviceTypes: async () => {
    try {
      console.log('Fetching device types from:', `${API_BASE_URL}/devicetypes/`);
      const response = await apiClient.get('/devicetypes/');
      console.log('Device types response:', response.data);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching device types:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch device types');
    }
  },

  // Get all units (for alert config scoping)
  getUnits: async (params = {}) => {
    try {
      const qs = new URLSearchParams({ page_size: '200', ...params }).toString();
      return await fetchAllPages(`/units/?${qs}`);
    } catch (error) {
      console.error('Error fetching units:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch units');
    }
  },

  // Site CRUD methods
  createSite: async (siteData) => {
    try {
      const response = await apiClient.post('/sites/', siteData);
      return response.data;
    } catch (error) {
      console.error('Error creating site:', error.response?.status, error.response?.data);
      // Extract detailed error message
      let errorMessage = 'Failed to create site';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.dash_type) {
          errorMessage = `Dashboard type error: ${JSON.stringify(error.response.data.dash_type)}`;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }
      throw new Error(errorMessage);
    }
  },

  updateSite: async (siteId, siteData) => {
    try {
      const response = await apiClient.put(`/sites/${siteId}/`, siteData);
      return response.data;
    } catch (error) {
      console.error('Error updating site:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to update site');
    }
  },

  deleteSite: async (siteId) => {
    try {
      await apiClient.delete(`/sites/${siteId}/`);
    } catch (error) {
      console.error('Error deleting site:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to delete site');
    }
  },

  // Unit CRUD methods
  createUnit: async (unitData) => {
    try {
      const response = await apiClient.post('/units/', unitData);
      return response.data;
    } catch (error) {
      console.error('Error creating unit:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create unit');
    }
  },

  updateUnit: async (unitId, unitData) => {
    try {
      const response = await apiClient.put(`/units/${unitId}/`, unitData);
      return response.data;
    } catch (error) {
      console.error('Error updating unit:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to update unit');
    }
  },

  deleteUnit: async (unitId) => {
    try {
      await apiClient.delete(`/units/${unitId}/`);
    } catch (error) {
      console.error('Error deleting unit:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to delete unit');
    }
  },

  // Get all devices (for alert config scoping)
  getDevices: async () => {
    try {
      return await fetchAllPages('/devices/?page_size=200');
    } catch (error) {
      console.error('Error fetching devices:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch devices');
    }
  },

  // Site Notes API methods
  // Get site note for a site (returns null if doesn't exist - don't auto-create)
  getSiteNote: async (siteId) => {
    try {
      const response = await apiClient.get(`/site-notes/?site=${siteId}`);
      const results = response.data.results || response.data;
      if (results.length > 0) {
        return results[0];
      }
      // Return null if no site note exists (don't auto-create)
      return null;
    } catch (error) {
      console.error('Error fetching site note:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch site note');
    }
  },
  
  // Create site note for a site
  createSiteNote: async (siteId) => {
    try {
      const createResponse = await apiClient.post('/site-notes/', { site: siteId });
      return createResponse.data;
    } catch (error) {
      console.error('Error creating site note:', error.response?.status, error.response?.data);
      throw new Error('Failed to create site note');
    }
  },

  // Upload or update site note image
  uploadSiteNoteImage: async (siteNoteId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await apiClient.post(
        `/site-notes/${siteNoteId}/upload_image/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error.response?.status, error.response?.data);
      throw new Error('Failed to upload image');
    }
  },
  
  // Delete site note image
  deleteSiteNoteImage: async (siteNoteId) => {
    try {
      const response = await apiClient.delete(`/site-notes/${siteNoteId}/delete_image/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting image:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete image');
    }
  },

  // Get site note markers
  getSiteNoteMarkers: async (siteNoteId) => {
    try {
      const response = await apiClient.get(`/site-note-markers/?site_note=${siteNoteId}`);
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching markers:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch markers');
    }
  },

  // Create site note marker
  createSiteNoteMarker: async (markerData) => {
    try {
      const response = await apiClient.post('/site-note-markers/', markerData);
      return response.data;
    } catch (error) {
      console.error('Error creating marker:', error.response?.status, error.response?.data);
      throw new Error('Failed to create marker');
    }
  },

  // Update site note marker
  updateSiteNoteMarker: async (markerId, markerData) => {
    try {
      const response = await apiClient.put(`/site-note-markers/${markerId}/`, markerData);
      return response.data;
    } catch (error) {
      console.error('Error updating marker:', error.response?.status, error.response?.data);
      throw new Error('Failed to update marker');
    }
  },

  // Delete site note marker
  deleteSiteNoteMarker: async (markerId) => {
    try {
      await apiClient.delete(`/site-note-markers/${markerId}/`);
    } catch (error) {
      console.error('Error deleting marker:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete marker');
    }
  },

  // Update site note notepad
  updateSiteNoteNotepad: async (siteNoteId, notepadContent) => {
    try {
      const response = await apiClient.patch(`/site-notes/${siteNoteId}/`, { notepad: notepadContent });
      return response.data;
    } catch (error) {
      console.error('Error updating notepad:', error.response?.status, error.response?.data);
      throw new Error('Failed to update notepad');
    }
  },

  // Building Profile API methods
  lookupBuildingProfile: async (siteId, address) => {
    try {
      const response = await apiClient.post('/building-profile/lookup/', {
        site_id: siteId,
        address
      });
      return response.data;
    } catch (error) {
      console.error('Error looking up building profile:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to look up building profile');
    }
  },

  updateBuildingProfile: async (siteSlug, profileData, address) => {
    try {
      const payload = { building_profile: profileData };
      if (address !== undefined) payload.address = address;
      const response = await apiClient.patch(`/sites/${siteSlug}/building-profile/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating building profile:', error.response?.status, error.response?.data);
      throw new Error('Failed to update building profile');
    }
  },

  // Update unit/property notes
  updateUnitNotes: async (unitId, notes) => {
    try {
      const response = await apiClient.patch(`/units/${unitId}/`, { notes });
      return response.data;
    } catch (error) {
      console.error('Error updating unit notes:', error.response?.status, error.response?.data);
      throw new Error('Failed to update property notes');
    }
  },

  // Bulk save unit note tabs (create/update/delete in one request).
  // Reserved tabs (e.g. survey notes) are managed via a dedicated endpoint —
  // strip them here so a stray inclusion doesn't 400 the whole bulk save.
  saveUnitNoteTabs: async (unitId, tabs) => {
    try {
      const editableTabs = (tabs || []).filter(t => !t.is_reserved);
      const response = await apiClient.put(`/units/${unitId}/note-tabs/`, { tabs: editableTabs });
      return response.data;
    } catch (error) {
      console.error('Error saving unit note tabs:', error.response?.status, error.response?.data);
      throw new Error('Failed to save property note tabs');
    }
  },

  // Upload image for notepad/rich-text editors
  uploadNotepadImage: async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const response = await apiClient.post('/notepad-images/', formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading notepad image:', error.response?.status, error.response?.data);
      throw new Error('Failed to upload image');
    }
  },

  // Report Request API methods
  createReportRequest: async (requestData) => {
    try {
      console.log('Creating report request:', requestData);
      const response = await apiClient.post('/report-requests/', requestData);
      console.log('Report request created:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating report request:', error.response?.status, error.response?.data);
      throw new Error('Failed to create report request');
    }
  },

  getReportRequests: async () => {
    try {
      const response = await apiClient.get('/report-requests/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching report requests:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch report requests');
    }
  },

  // Mark alert as read (does not acknowledge — just hides from notification bell)
  markAlertRead: async (alertId) => {
    try {
      const response = await apiClient.patch(`/alerts/${alertId}/`, { read: true });
      return response.data;
    } catch (error) {
      console.error('Error marking alert as read:', error.response?.status, error.response?.data);
      throw new Error('Failed to mark alert as read');
    }
  },

  // Acknowledge alert
  acknowledgeAlert: async (alertId) => {
    try {
      const response = await apiClient.patch(`/alerts/${alertId}/`, {
        ts_acknowledged: new Date().toISOString(),
        read: true
      });
      return response.data;
    } catch (error) {
      console.error('Error acknowledging alert:', error.response?.status, error.response?.data);
      throw new Error('Failed to acknowledge alert');
    }
  },

  // Unacknowledge alert (mark as unseen)
  unacknowledgeAlert: async (alertId) => {
    try {
      const response = await apiClient.patch(`/alerts/${alertId}/`, {
        ts_acknowledged: null,
        read: false
      });
      return response.data;
    } catch (error) {
      console.error('Error unacknowledging alert:', error.response?.status, error.response?.data);
      throw new Error('Failed to unacknowledge alert');
    }
  },

  // Resolve alert
  resolveAlert: async (alertId) => {
    try {
      const response = await apiClient.patch(`/alerts/${alertId}/`, {
        ts_closed: new Date().toISOString(),
        read: true
      });
      return response.data;
    } catch (error) {
      console.error('Error resolving alert:', error.response?.status, error.response?.data);
      throw new Error('Failed to resolve alert');
    }
  },

  // Device CRUD methods
  createDevice: async (deviceData) => {
    try {
      const response = await apiClient.post('/devices/', deviceData);
      return response.data;
    } catch (error) {
      console.error('Error creating device:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create device');
    }
  },

  updateDevice: async (deviceId, deviceData) => {
    try {
      const response = await apiClient.put(`/devices/${deviceId}/`, deviceData);
      return response.data;
    } catch (error) {
      console.error('Error updating device:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to update device');
    }
  },

  deleteDevice: async (deviceId) => {
    try {
      await apiClient.delete(`/devices/${deviceId}/`);
    } catch (error) {
      console.error('Error deleting device:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete device');
    }
  },

  // FuseConfig CRUD methods (for FUSEBOARD_UDC devices)
  createFuseConfig: async (fuseConfigData) => {
    try {
      const response = await apiClient.post('/fuse-configs/', fuseConfigData);
      return response.data;
    } catch (error) {
      console.error('Error creating fuse config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create fuse config');
    }
  },

  updateFuseConfig: async (fuseConfigId, fuseConfigData) => {
    try {
      const response = await apiClient.put(`/fuse-configs/${fuseConfigId}/`, fuseConfigData);
      return response.data;
    } catch (error) {
      console.error('Error updating fuse config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to update fuse config');
    }
  },

  deleteFuseConfig: async (fuseConfigId) => {
    try {
      await apiClient.delete(`/fuse-configs/${fuseConfigId}/`);
    } catch (error) {
      console.error('Error deleting fuse config:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete fuse config');
    }
  },

  // SDM630 Phase Config methods
  createSDM630PhaseConfig: async (phaseConfigData) => {
    try {
      const response = await apiClient.post('/sdm630-phase-configs/', phaseConfigData);
      return response.data;
    } catch (error) {
      console.error('Error creating SDM630 phase config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create SDM630 phase config');
    }
  },

  updateSDM630PhaseConfig: async (phaseConfigId, phaseConfigData) => {
    try {
      const response = await apiClient.patch(`/sdm630-phase-configs/${phaseConfigId}/`, phaseConfigData);
      return response.data;
    } catch (error) {
      console.error('Error updating SDM630 phase config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to update SDM630 phase config');
    }
  },

  deleteSDM630PhaseConfig: async (phaseConfigId) => {
    try {
      await apiClient.delete(`/sdm630-phase-configs/${phaseConfigId}/`);
    } catch (error) {
      console.error('Error deleting SDM630 phase config:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete SDM630 phase config');
    }
  },

  // Unmonitored Circuit methods
  createUnmonitoredCircuit: async (circuitData) => {
    try {
      const response = await apiClient.post('/unmonitored-circuits/', circuitData);
      return response.data;
    } catch (error) {
      console.error('Error creating unmonitored circuit:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create unmonitored circuit');
    }
  },

  updateUnmonitoredCircuit: async (circuitId, circuitData) => {
    try {
      const response = await apiClient.patch(`/unmonitored-circuits/${circuitId}/`, circuitData);
      return response.data;
    } catch (error) {
      console.error('Error updating unmonitored circuit:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to update unmonitored circuit');
    }
  },

  deleteUnmonitoredCircuit: async (circuitId) => {
    try {
      await apiClient.delete(`/unmonitored-circuits/${circuitId}/`);
    } catch (error) {
      console.error('Error deleting unmonitored circuit:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete unmonitored circuit');
    }
  },

  // Voltage optimiser installation methods
  getVoltageOptimiserInstallations: async () => {
    try {
      const response = await apiClient.get('/voltage-optimiser-installations/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching optimiser installations:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch optimiser installations');
    }
  },

  createVoltageOptimiserInstallation: async (data) => {
    try {
      const response = await apiClient.post('/voltage-optimiser-installations/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating optimiser installation:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create optimiser installation');
    }
  },

  updateVoltageOptimiserInstallation: async (installationId, data) => {
    try {
      const response = await apiClient.patch(`/voltage-optimiser-installations/${installationId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating optimiser installation:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to update optimiser installation');
    }
  },

  /**
   * Superuser: switch own UserProfile.org by PATCHing self via the existing admin users endpoint.
   * Composes existing routes — no new backend code. Caller should hard-refresh after success
   * so every context (BrandingContext etc.) reloads in the new org's data.
   */
  switchOrg: async (currentUserEmail, newOrgId) => {
    if (!currentUserEmail) throw new Error('Cannot switch org without current user email');
    const response = await apiClient.get('/admin/users/', {
      params: { search: currentUserEmail, page_size: 5 },
    });
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.results || []);
    const me = list.find(u => (u.email || '').toLowerCase() === currentUserEmail.toLowerCase());
    if (!me) throw new Error('Could not locate current user record');
    const putResp = await apiClient.put(`/admin/users/${me.id}/`, { ...me, org: newOrgId });
    return putResp.data;
  },

  // Get organizations (for superusers, returns all orgs; for regular users, returns their org)
  getOrgs: async () => {
    try {
      const response = await apiClient.get('/orgs/');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch organizations');
    }
  },

  // Create a new organization (superuser only)
  createOrg: async (orgData) => {
    try {
      const response = await apiClient.post('/orgs/', orgData);
      return response.data;
    } catch (error) {
      console.error('Error creating organization:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to create organization');
    }
  },

  // Update an organization (superuser only)
  updateOrg: async (id, orgData) => {
    try {
      const response = await apiClient.patch(`/orgs/${id}/`, orgData);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to update organization');
    }
  },

  // Delete an organization (superuser only)
  deleteOrg: async (id) => {
    try {
      await apiClient.delete(`/orgs/${id}/`);
    } catch (error) {
      console.error('Error deleting organization:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to delete organization');
    }
  },

  // Get organization settings
  getOrgSettings: async () => {
    try {
      // Use dedicated user org settings endpoint (automatically uses user's org)
      const response = await apiClient.get('/user/org-settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching org settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch org settings');
    }
  },

  // Update organization settings with all fields including white-label
  updateOrgSettingsWithWhiteLabel: async (settings) => {
    try {
      const response = await apiClient.put('/user/org-settings/', settings);
      return response.data;
    } catch (error) {
      console.error('Error updating org settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to update org settings');
    }
  },

  // Upload organization logo for white-labeling
  uploadOrgLogo: async (file) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiClient.post('/org/logo/', formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading org logo:', error.response?.status, error.response?.data);
      throw new Error('Failed to upload organization logo');
    }
  },

  // Delete organization logo
  deleteOrgLogo: async () => {
    try {
      const response = await apiClient.delete('/org/logo/');
      return response.data;
    } catch (error) {
      console.error('Error deleting org logo:', error.response?.status, error.response?.data);
      throw new Error('Failed to delete organization logo');
    }
  },

  // Get site settings
  getSiteSettings: async (siteId) => {
    try {
      const response = await apiClient.get(`/sites/${siteId}/settings/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch site settings');
    }
  },

  // Update site settings
  updateSiteSettings: async (siteId, unitRateOverride, intervalMinutesOverride, carbonFactorOverride) => {
    try {
      const response = await apiClient.put(`/sites/${siteId}/settings/`, {
        unit_rate_override: unitRateOverride || null,
        interval_minutes_override: intervalMinutesOverride || null,
        carbon_factor_kg_per_kwh_override: carbonFactorOverride || null
      });
      return response.data;
    } catch (error) {
      console.error('Error updating site settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to update site settings');
    }
  },

  // User Settings
  getUserSettings: async () => {
    try {
      const response = await apiClient.get('/user/settings/');
      return response.data;
    } catch (error) {
      console.error('Error fetching user settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch user settings');
    }
  },

  updateUserSettings: async (settings) => {
    try {
      // Support both old signature (plain object = report_visibility) and new signature (general settings)
      const payload = settings && (settings.report_visibility !== undefined || settings.ai_assistant_enabled !== undefined)
        ? settings
        : { report_visibility: settings };
      const response = await apiClient.put('/user/settings/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating user settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to update user settings');
    }
  },

  // Update org settings to include default_report_visibility and offline_threshold_minutes
  updateOrgSettingsWithDefaults: async (unitRate, intervalMinutes, carbonFactor, defaultReportVisibility, offlineThresholdMinutes, homeDashboardKPIs = null, offlineAlertsEnabled = null, offlineAlertEmails = null, pricingSchedule = null, defaultAihubVisibility = null) => {
    try {
      // Use dedicated user org settings endpoint (automatically uses user's org)
      const payload = {
        unit_rate: unitRate,
        interval_minutes: intervalMinutes,
        carbon_factor_kg_per_kwh: carbonFactor,
        default_report_visibility: defaultReportVisibility,
        offline_threshold_minutes: offlineThresholdMinutes
      };
      if (homeDashboardKPIs !== null && homeDashboardKPIs !== undefined) {
        payload.home_dashboard_kpis = homeDashboardKPIs;
      }
      if (offlineAlertsEnabled !== null && offlineAlertsEnabled !== undefined) {
        payload.offline_alerts_enabled = offlineAlertsEnabled;
      }
      if (offlineAlertEmails !== null && offlineAlertEmails !== undefined) {
        payload.offline_alert_emails = offlineAlertEmails;
      }
      if (pricingSchedule !== null && pricingSchedule !== undefined) {
        payload.pricing_schedule = pricingSchedule;
      }
      if (defaultAihubVisibility !== null && defaultAihubVisibility !== undefined) {
        payload.default_aihub_visibility = defaultAihubVisibility;
      }
      const response = await apiClient.put('/user/org-settings/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating org settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to update organization settings');
    }
  },

  // Update organization settings with flexible fields (for KPI configs, etc)
  updateOrgSettings: async (settings) => {
    try {
      const payload = {
        unit_rate: settings.unit_rate,
        interval_minutes: settings.interval_minutes,
        carbon_factor_kg_per_kwh: settings.carbon_factor_kg_per_kwh,
        default_report_visibility: settings.default_report_visibility,
        offline_threshold_minutes: settings.offline_threshold_minutes,
        home_dashboard_kpis: settings.home_dashboard_kpis,
        fuseboard_dashboard_kpis: settings.fuseboard_dashboard_kpis,
        environmental_dashboard_kpis: settings.environmental_dashboard_kpis,
      };
      if (settings.default_aihub_visibility !== undefined) {
        payload.default_aihub_visibility = settings.default_aihub_visibility;
      }
      const response = await apiClient.put('/user/org-settings/', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating org settings:', error.response?.status, error.response?.data);
      throw new Error('Failed to update organization settings');
    }
  },

  // Offline Devices API
  getOfflineDevices: async () => {
    try {
      const response = await apiClient.get('/offline-devices/');
      return response.data;
    } catch (error) {
      console.error('Error fetching offline devices:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch offline devices');
    }
  },

  acknowledgeOfflineDevice: async (deviceId, acknowledge = true, lastSeen = null) => {
    try {
      const response = await apiClient.post('/offline-devices/', {
        device_id: deviceId,
        acknowledge: acknowledge,
        last_seen: lastSeen
      });
      return response.data;
    } catch (error) {
      console.error('Error acknowledging offline device:', error.response?.status, error.response?.data);
      throw new Error('Failed to acknowledge offline device');
    }
  },


  // Get cost savings
  getCostSavings: async (siteId = null, { includeTrends = false } = {}) => {
    try {
      let url = siteId ? `/cost-savings/?site_id=${siteId}` : '/cost-savings/';
      if (includeTrends) url += (url.includes('?') ? '&' : '?') + 'include_trends=true';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching cost savings:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch cost savings');
    }
  },

  // Get energy savings KPIs (SDM230/630 + CS01-NB combined)
  getEnergySavingsKPIs: async (siteId = null, period = 'lifetime') => {
    try {
      // Add cache-busting timestamp to prevent stale data
      const timestamp = new Date().getTime();
      const url = siteId 
        ? `/energy-savings-kpis/?site_id=${siteId}&period=${period}&_t=${timestamp}` 
        : `/energy-savings-kpis/?period=${period}&_t=${timestamp}`;
      console.log('Fetching energy savings KPIs from:', url);
      const response = await apiClient.get(url);
      console.log('Energy savings KPIs response:', response.data);
      console.log(`  Energy: ${response.data.energy_saved_kwh} kWh`);
      console.log(`  Carbon: ${response.data.carbon_saved_kg} kg`);
      console.log(`  Money: £${response.data.money_saved_gbp}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching energy savings KPIs:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      // Return zeros if endpoint fails (graceful degradation)
      return {
        energy_saved_kwh: 0,
        carbon_saved_kg: 0,
        money_saved_gbp: 0
      };
    }
  },

  getEnergySavingsRecords: async (installationId, startDate = null, endDate = null) => {
    try {
      let url = `/energy-savings-records/?installation_id=${installationId}`;
      if (startDate) {
        url += `&start_date=${startDate.toISOString()}`;
      }
      if (endDate) {
        url += `&end_date=${endDate.toISOString()}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching energy savings records:', error);
      return [];
    }
  },

  // Get environmental KPIs aggregated across the org (or a specific site)
  getEnvironmentalKPIs: async (siteId = null, { includeTrends = false } = {}) => {
    try {
      let url = siteId ? `/environmental-kpis/?site_id=${siteId}` : '/environmental-kpis/';
      if (includeTrends) url += (url.includes('?') ? '&' : '?') + 'include_trends=true';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching environmental KPIs:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch environmental KPIs');
    }
  },

  // Water Monitoring (Quandify Cubic) — KPIs aggregated across org or site
  getWaterKPIs: async (siteId = null) => {
    try {
      const url = siteId ? `/water-kpis/?site_id=${siteId}` : '/water-kpis/';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching water KPIs:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch water KPIs');
    }
  },

  // Water Monitoring — full dashboard payload (site → units → devices → readings)
  getWaterDashboardData: async (siteId = null) => {
    try {
      const url = siteId ? `/water/dashboard-data/?site_id=${siteId}` : '/water/dashboard-data/';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching water dashboard data:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch water dashboard data');
    }
  },

  // Water Monitoring — read or update a device's pipe-type config
  getWaterDeviceConfig: async (deviceId) => {
    try {
      const response = await apiClient.get(`/water-device-config/${deviceId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching water device config:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch water device config');
    }
  },

  updateWaterDeviceConfig: async (deviceId, payload) => {
    try {
      const response = await apiClient.put(`/water-device-config/${deviceId}/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating water device config:', error.response?.status, error.response?.data);
      throw new Error('Failed to update water device config');
    }
  },

  // Water Monitoring Report — KPI summary + per-property table rows
  getWaterMonitoringReportSummary: async (siteId = null, timeRange = '30d') => {
    try {
      const params = new URLSearchParams({ time_range: timeRange });
      if (siteId) params.append('site_id', siteId);
      const response = await apiClient.get(`/water-monitoring-report/summary/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching water monitoring report summary:', error.response?.status);
      throw new Error('Failed to fetch water monitoring report summary');
    }
  },

  // Water Monitoring Report — daily-bucketed time-series for trend + flow charts
  getWaterMonitoringReportTrends: async (siteId = null, timeRange = '30d') => {
    try {
      const params = new URLSearchParams({ time_range: timeRange });
      if (siteId) params.append('site_id', siteId);
      const response = await apiClient.get(`/water-monitoring-report/trends/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching water monitoring report trends:', error.response?.status);
      throw new Error('Failed to fetch water monitoring report trends');
    }
  },

  // Water Monitoring Report — HSG274 / L8 compliance scorecards
  getWaterMonitoringReportCompliance: async (siteId = null, timeRange = '30d') => {
    try {
      const params = new URLSearchParams({ time_range: timeRange });
      if (siteId) params.append('site_id', siteId);
      const response = await apiClient.get(`/water-monitoring-report/compliance/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching water monitoring report compliance:', error.response?.status);
      throw new Error('Failed to fetch water monitoring report compliance');
    }
  },

  getRecommendedActions: async (siteId = null, timeRange = '30d', category = null, priority = null, unitId = null) => {
    try {
      let url = `/reports/recommended-actions/?time_range=${timeRange}`;
      if (siteId) url += `&site_id=${siteId}`;
      if (unitId) url += `&unit_id=${unitId}`;
      if (category) url += `&category=${category}`;
      if (priority) url += `&priority=${priority}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching recommended actions:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch recommended actions');
    }
  },

  // Get AI-generated improvement plans
  getImprovementPlans: async (siteId = null, timeRange = '30d', unitId = null) => {
    try {
      let url = `/reports/improvement-plans/?time_range=${timeRange}`;
      if (siteId) url += `&site_id=${siteId}`;
      if (unitId) url += `&unit_id=${unitId}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching improvement plans:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch improvement plans');
    }
  },

  // Detect anomalies via rule-based backend engine
  detectAnomalies: async (siteId = null, timeRange = '24h', types = null, sensitivity = 'medium', unitId = null) => {
    try {
      let url = `/reports/anomaly-detection/?time_range=${timeRange}&sensitivity=${sensitivity}`;
      if (siteId) url += `&site_id=${siteId}`;
      if (types) url += `&types=${types}`;
      if (unitId) url += `&unit_id=${unitId}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error detecting anomalies:', error.response?.status, error.response?.data);
      throw new Error('Failed to detect anomalies');
    }
  },

  // Get sensor health report data
  getSensorHealthReport: async (siteId = null, period = '7d') => {
    try {
      let url = `/reports/sensor-health/?period=${period}`;
      if (siteId) url += `&site_id=${siteId}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor health report:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch sensor health report');
    }
  },

  // Get sensor health trend data (daily aggregated fleet health)
  getSensorHealthTrends: async (siteId = null, period = '30d') => {
    try {
      let url = `/reports/sensor-health/trends/?period=${period}`;
      if (siteId) url += `&site_id=${siteId}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching sensor health trends:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch sensor health trends');
    }
  },

  // Get sensor health battery forecast (per-device depletion prediction)
  getSensorHealthBatteryForecast: async (siteId = null) => {
    try {
      let url = '/reports/sensor-health/battery-forecast/';
      if (siteId) url += `?site_id=${siteId}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching battery forecast:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch battery forecast');
    }
  },

  // Get league table data (server-side aggregation)
  getLeagueTable: async ({ siteId, parameters, timeRange = '30d', startDate, endDate, sort, direction, search, tier, page = 1, pageSize = 25 } = {}) => {
    try {
      let url = `/devices/league-table/?site_id=${siteId}&parameters=${parameters}&time_range=${timeRange}&page=${page}&page_size=${pageSize}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      if (sort) url += `&sort=${sort}`;
      if (direction) url += `&direction=${direction}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (tier) url += `&tier=${tier}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching league table:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch league table');
    }
  },

  // Get devices at risk for modal display
  getDevicesAtRisk: async () => {
    try {
      const response = await apiClient.get('/devices-at-risk/');
      return response.data;
    } catch (error) {
      console.error('Error fetching devices at risk:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch devices at risk');
    }
  },

  // Get Awaab's Law violations for modal display
  getAwaabsLawViolations: async () => {
    try {
      const response = await apiClient.get('/awaabs-law-violations/');
      return response.data;
    } catch (error) {
      console.error('Error fetching Awaab\'s Law violations:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch Awaab\'s Law violations');
    }
  },

  // Get AI insights for fuseboard devices at a site
  getFuseboardInsights: async (siteId, capabilities = [], unitId = null, deviceId = null, userMessage = null, contextInfo = null) => {
    try {
      const params = new URLSearchParams();
      if (siteId) {
        params.append('site_id', siteId);
      }
      if (capabilities && capabilities.length > 0) {
        // Add capabilities as multiple query params (Django getlist format)
        capabilities.forEach(cap => {
          params.append('capabilities', cap);
        });
      }
      if (unitId) {
        params.append('unit_id', unitId);
      }
      if (deviceId) {
        params.append('device_id', deviceId);
      }
      if (userMessage) {
        params.append('user_message', userMessage);
      }
      if (contextInfo) {
        params.append('context_info', JSON.stringify(contextInfo));
      }
      const response = await apiClient.get(`/fuseboard-insights/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching fuseboard insights:', error);
      throw error;
    }
  },

  // Get AI insights for a single device
  getDeviceInsights: async (deviceId) => {
    try {
      const response = await apiClient.get('/device-insights/', {
        params: { device_id: deviceId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching device insights:', error);
      throw error;
    }
  },

  // Get unit risk summary (DB-only pre-filter for AI wizard property selection)
  getUnitRiskSummary: async (siteId, capabilities = []) => {
    try {
      const params = new URLSearchParams();
      params.append('site_id', siteId);
      if (capabilities && capabilities.length > 0) {
        capabilities.forEach(cap => params.append('capabilities', cap));
      }
      const response = await apiClient.get(`/unit-risk-summary/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching unit risk summary:', error);
      throw error;
    }
  },

  // Get AI insights for a property/unit (aggregates all device readings)
  getPropertyInsights: async (unitId, capabilities = [], deviceId = null, siteId = null, userMessage = null, contextInfo = null, unitIds = null) => {
    try {
      const params = new URLSearchParams();
      // Multi-unit path: send comma-separated unit_ids
      if (unitIds && Array.isArray(unitIds) && unitIds.length > 1) {
        params.append('unit_ids', unitIds.join(','));
      } else if (unitId !== null && unitId !== undefined && unitId !== 'null' && unitId !== '') {
        // Only append unit_id if it's not null/undefined/empty
        params.append('unit_id', unitId);
      }
      // Append site_id if provided (for site-level aggregation when no unit selected)
      if (siteId !== null && siteId !== undefined && siteId !== 'null' && siteId !== '') {
        params.append('site_id', siteId);
      }
      if (capabilities && capabilities.length > 0) {
        capabilities.forEach(cap => {
          params.append('capabilities', cap);
        });
      }
      if (deviceId) {
        params.append('device_id', deviceId);
      }
      // Add user message for assistant-chat capability
      if (userMessage) {
        params.append('user_message', userMessage);
      }
      // Add context_info as JSON string
      if (contextInfo) {
        params.append('context_info', JSON.stringify(contextInfo));
      }
      const response = await apiClient.get(`/property-insights/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property insights:', error);
      throw error;
    }
  },

  // Get property forecasts (historical trends + AI projections)
  getPropertyForecasts: async (siteId, unitIds = [], metrics = []) => {
    try {
      const params = new URLSearchParams();
      params.append('site_id', siteId);
      if (unitIds && unitIds.length > 0) {
        params.append('unit_ids', unitIds.join(','));
      }
      metrics.forEach(m => params.append('metrics', m));
      const response = await apiClient.get(`/property-forecasts/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property forecasts:', error);
      throw error;
    }
  },

  // Get compliance summary for AI Hub analysis
  getComplianceSummary: async (siteId, unitIds = []) => {
    try {
      const params = new URLSearchParams();
      if (siteId !== null && siteId !== undefined && siteId !== 'null' && siteId !== '') {
        params.append('site_id', siteId);
      }
      if (unitIds && Array.isArray(unitIds) && unitIds.length > 0) {
        params.append('unit_ids', unitIds.join(','));
      }
      const response = await apiClient.get(`/compliance-summary/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('[AIHub] Error fetching compliance summary:', error);
      throw error;
    }
  },

  // Scheduled Reports API methods
  getScheduledReports: async () => {
    try {
      const response = await apiClient.get('/scheduled-reports/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching scheduled reports:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to fetch scheduled reports');
    }
  },

  createScheduledReport: async (reportData) => {
    try {
      const response = await apiClient.post('/scheduled-reports/', reportData);
      return response.data;
    } catch (error) {
      console.error('Error creating scheduled report:', error.response?.status, error.response?.data);
      // Extract detailed error message
      const errorData = error.response?.data;
      let errorMessage = 'Failed to create scheduled report';
      
      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages.join(', ') : messages;
              return `${field}: ${msg}`;
            })
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  updateScheduledReport: async (reportId, reportData) => {
    try {
      const response = await apiClient.put(`/scheduled-reports/${reportId}/`, reportData);
      return response.data;
    } catch (error) {
      console.error('Error updating scheduled report:', error.response?.status, error.response?.data);
      // Extract detailed error message
      const errorData = error.response?.data;
      let errorMessage = 'Failed to update scheduled report';
      
      if (errorData) {
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'object') {
          // Handle field-specific errors
          const fieldErrors = Object.entries(errorData)
            .map(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages.join(', ') : messages;
              return `${field}: ${msg}`;
            })
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  deleteScheduledReport: async (reportId) => {
    try {
      await apiClient.delete(`/scheduled-reports/${reportId}/`);
    } catch (error) {
      console.error('Error deleting scheduled report:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to delete scheduled report');
    }
  },

  testScheduledReport: async (reportId) => {
    try {
      const response = await apiClient.post(`/scheduled-reports/${reportId}/test/`);
      return response.data;
    } catch (error) {
      console.error('Error testing scheduled report:', error.response?.status, error.response?.data);
      // Extract detailed error message
      const errorData = error.response?.data;
      let errorMessage = 'Failed to test scheduled report';
      
      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
      
      throw new Error(errorMessage);
    }
  },

  // Map Building Configuration methods
  getMapConfigurations: async () => {
    try {
      const response = await apiClient.get('/map-configurations/');
      return response.data.results || response.data;
    } catch (error) {
      console.error('Error fetching map configurations:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch map configurations');
    }
  },

  createMapConfiguration: async (configData) => {
    try {
      const response = await apiClient.post('/map-configurations/', configData);
      return response.data;
    } catch (error) {
      console.error('Error creating map configuration:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to create map configuration';
      throw new Error(errorMessage);
    }
  },

  updateMapConfiguration: async (id, configData) => {
    try {
      const response = await apiClient.put(`/map-configurations/${id}/`, configData);
      return response.data;
    } catch (error) {
      console.error('Error updating map configuration:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to update map configuration';
      throw new Error(errorMessage);
    }
  },

  deleteMapConfiguration: async (id) => {
    try {
      await apiClient.delete(`/map-configurations/${id}/`);
    } catch (error) {
      console.error('Error deleting map configuration:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to delete map configuration';
      throw new Error(errorMessage);
    }
  },

  getMapConfigAlertStatus: async (id) => {
    try {
      const response = await apiClient.get(`/map-configurations/${id}/alert-status/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching map config alert status:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch alert status');
    }
  },

  getBulkMapConfigAlertStatuses: async (configIds) => {
    try {
      const response = await apiClient.post('/map-configurations/alert-statuses/', {
        config_ids: configIds
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk alert statuses:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch alert statuses');
    }
  },

  // Installer methods
  getNetworkServerDevices: async (boxNumber) => {
    try {
      const response = await apiClient.get(`/network-server/devices/${boxNumber}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network server devices:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to fetch devices from network server';
      throw new Error(errorMessage);
    }
  },

  createInstallerDevices: async (data) => {
    try {
      const response = await apiClient.post('/installer/devices/', data);
      return response.data;
    } catch (error) {
      console.error('Error creating installer devices:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to create devices';
      throw new Error(errorMessage);
    }
  },

  // ============================================
  // USER MANAGEMENT (Superuser Only)
  // ============================================

  /**
   * Get all users with optional filters
   * @param {Object} params - Query parameters { search, org, user_type, status }
   * @returns {Promise<Array>} Array of users
   */
  getUsers: async (params = {}) => {
    try {
      const response = await apiClient.get('/admin/users/', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting users:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to load users';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get a specific user by ID
   * @param {number} userId - User ID
   * @returns {Promise<Object>} User object
   */
  getUser: async (userId) => {
    try {
      const response = await apiClient.get(`/admin/users/${userId}/`);
      return response.data;
    } catch (error) {
      console.error('Error getting user:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to load user';
      throw new Error(errorMessage);
    }
  },

  /**
   * Create a new user
   * @param {Object} userData - User data { username, email, first_name, last_name, is_staff, is_superuser, is_active, org }
   * @returns {Promise<Object>} Created user object
   */
  createUser: async (userData) => {
    try {
      const response = await apiClient.post('/admin/users/', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         error.response?.data?.username?.[0] ||
                         error.response?.data?.email?.[0] ||
                         'Failed to create user';
      throw new Error(errorMessage);
    }
  },

  /**
   * Update an existing user
   * @param {number} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user object
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`/admin/users/${userId}/`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         error.response?.data?.username?.[0] ||
                         error.response?.data?.email?.[0] ||
                         'Failed to update user';
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a user
   * @param {number} userId - User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (userId) => {
    try {
      await apiClient.delete(`/admin/users/${userId}/`);
    } catch (error) {
      console.error('Error deleting user:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to delete user';
      throw new Error(errorMessage);
    }
  },

  // Get mould risk report summary with optional trends
  getMouldRiskSummaryTrends: async (siteId = null, unitIds = null) => {
    try {
      let url = '/reports/mould-risk/summary/?include_trends=true';
      if (siteId) url += `&site_id=${siteId}`;
      if (unitIds) url += `&unit_ids=${unitIds}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching mould risk summary trends:', error.response?.status);
      return null;
    }
  },

  // ========== Mould Risk Evidence Pack Methods ==========

  /**
   * Validate a unit for evidence pack generation
   * @param {number} unitId - Unit ID
   * @returns {Promise<Object>} Validation result with sensors and data availability
   */
  validateUnitForEvidencePack: async (unitId) => {
    try {
      const response = await apiClient.get(`/evidence-packs/validate-unit/${unitId}/`);
      return response.data;
    } catch (error) {
      console.error('Error validating unit for evidence pack:', error.response?.status, error.response?.data);
      const errorMessage = error.response?.data?.error || 
                         error.response?.data?.detail || 
                         'Failed to validate unit';
      throw new Error(errorMessage);
    }
  },

  /**
   * Generate mould risk evidence pack
   * @param {Object} config - Evidence pack configuration
   *   {
   *     unit_id: number,
   *     date_range: string ('28d', '90d', '180d'),
   *     photos: Array of photo objects with base64 data,
   *     additional_info: {tenant_name, tenant_email, etc.}
   *   }
   * @returns {Promise<Blob>} ZIP file blob
   */
  generateEvidencePack: async (config) => {
    try {
      const response = await apiClient.post('/evidence-packs/generate/', config, {
        responseType: 'blob', // Important for binary data
        timeout: 120000 // 2 minutes timeout for large packs
      });
      return response.data;
    } catch (error) {
      console.error('Error generating evidence pack:', error.response?.status, error.response?.data);

      // For blob responses, errors may be in blob format
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.detail || 'Failed to generate evidence pack');
        } catch (parseError) {
          throw new Error('Failed to generate evidence pack');
        }
      }

      const errorMessage = error.response?.data?.error ||
                         error.response?.data?.detail ||
                         'Failed to generate evidence pack';
      throw new Error(errorMessage);
    }
  },

  // Get air quality KPIs aggregated across the org (or a specific site)
  getAirQualityKPIs: async (siteId = null, { includeTrends = false } = {}) => {
    try {
      let url = siteId ? `/air-quality-kpis/?site_id=${siteId}` : '/air-quality-kpis/';
      if (includeTrends) url += (url.includes('?') ? '&' : '?') + 'include_trends=true';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching air quality KPIs:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch air quality KPIs');
    }
  },
  // ============================================
  // PDF Export (server-side Playwright)
  // ============================================

  /**
   * Request a server-side PDF export via Playwright.
   * @param {string} reportType - e.g. 'savings', 'league-table', 'mould-risk'
   * @param {string} timeRange - e.g. '30d', '7d'
   * @param {Object} parameters - Extra params (site_id, etc.)
   * @returns {Promise<{task_id: string, status: string}>}
   */
  requestPDFExport: async (reportType, timeRange = '30d', parameters = {}) => {
    try {
      const response = await apiClient.post('/reports/export-pdf/', {
        report_type: reportType,
        time_range: timeRange,
        parameters,
      });
      return response.data;
    } catch (error) {
      console.error('Error requesting PDF export:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to request PDF export');
    }
  },

  /**
   * Poll the status of a PDF export task.
   * @param {string} taskId
   * @returns {Promise<{status: string, download_url?: string, filename?: string, error?: string}>}
   */
  checkPDFExport: async (taskId) => {
    try {
      const response = await apiClient.get(`/reports/export-pdf/${taskId}/`);
      return response.data;
    } catch (error) {
      console.error('Error checking PDF export:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to check PDF export status');
    }
  },

  /**
   * Download a PDF blob using the authenticated apiClient.
   * @param {string} url - Relative or absolute download URL
   * @returns {Promise<Blob>} PDF blob
   */
  downloadPDFBlob: async (url) => {
    try {
      // S3 pre-signed URLs are already absolute — fetch directly without auth headers
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.blob();
      }
      // Relative URLs go through apiClient (strips /api prefix since baseURL includes it)
      const cleanUrl = url.replace(/^\/api\//, '/');
      const response = await apiClient.get(cleanUrl, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('Error downloading PDF:', error.response?.status || error.message, error.response?.data);
      throw new Error('Failed to download PDF');
    }
  },

  // Bulk device readings — single POST replaces many per-device GETs
  getBulkDeviceReadings: async (deviceIds, limit = 20) => {
    try {
      const response = await apiClient.post('/devices/bulk-readings/', {
        device_ids: deviceIds,
        limit
      });
      return response.data; // { deviceId: [readings...] }
    } catch (error) {
      console.error('Error fetching bulk device readings:', error.response?.status, error.response?.data);
      throw new Error('Failed to fetch bulk device readings');
    }
  },

  // ============================================
  // Compliance Case Management (Phase 10+)
  // ============================================

  /**
   * Get org compliance config
   * @param {number} orgId
   * @returns {Promise<Object>} Org compliance config
   */
  getOrgComplianceConfig: async (orgId) => {
    try {
      const response = await apiClient.get(`/org-compliance-config/${orgId}/`);
      return response.data;
    } catch (error) {
      // 404 is expected for orgs that haven't set up compliance yet (opt-in feature)
      // Only log actual errors, not expected 404s
      if (error.response?.status !== 404) {
        console.error('Error fetching org compliance config:', error.response?.status, error.response?.data);
      }
      throw new Error('Failed to fetch compliance configuration');
    }
  },

  /**
   * Update org compliance config
   * @param {number} orgId
   * @param {Object} data - Config data to update
   * @returns {Promise<Object>} Updated config
   */
  updateOrgComplianceConfig: async (orgId, data) => {
    try {
      const response = await apiClient.put(`/org-compliance-config/${orgId}/`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating org compliance config:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to update compliance configuration');
    }
  },

  /**
   * Upload survey quote template
   * @param {number} orgId
   * @param {File} file - DOCX file
   * @returns {Promise<Object>} Updated config
   */
  uploadSurveyTemplate: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('survey_quote_template', file);
      const response = await apiClient.post(
        `/org-compliance-config/${orgId}/upload-survey-template/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading survey template:', error.response?.status, error.response?.data);
      throw new Error('Failed to upload survey template');
    }
  },

  /**
   * Upload remedial quote template
   * @param {number} orgId
   * @param {File} file - DOCX file
   * @returns {Promise<Object>} Updated config
   */
  uploadRemedialTemplate: async (orgId, file) => {
    try {
      const formData = new FormData();
      formData.append('remedial_quote_template', file);
      const response = await apiClient.post(
        `/org-compliance-config/${orgId}/upload-remedial-template/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading remedial template:', error.response?.status, error.response?.data);
      throw new Error('Failed to upload remedial template');
    }
  },

  /**
   * Complete compliance wizard setup
   * @param {number} orgId
   * @returns {Promise<Object>} Updated config
   */
  completeComplianceWizard: async (orgId) => {
    try {
      const response = await apiClient.post(
        `/org-compliance-config/${orgId}/complete-wizard/`
      );
      return response.data;
    } catch (error) {
      console.error('Error completing compliance wizard:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to complete wizard setup');
    }
  },

  /**
   * Download blank survey quote template
   * @returns {Promise<Blob>} DOCX file blob
   */
  downloadSurveyQuoteTemplate: async () => {
    try {
      const response = await apiClient.get('/org-compliance-config/download-survey-template/', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading survey template:', error.response?.status);
      throw new Error('Failed to download survey template');
    }
  },

  /**
   * Download blank remedial quote template
   * @returns {Promise<Blob>} DOCX file blob
   */
  downloadRemedialQuoteTemplate: async () => {
    try {
      const response = await apiClient.get('/org-compliance-config/download-remedial-template/', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading remedial template:', error.response?.status);
      throw new Error('Failed to download remedial template');
    }
  },

  /**
   * Get compliance cases (org-scoped)
   * @param {Object} filters - Query filters (status, unit_id)
   * @returns {Promise<Object>} Paginated compliance cases
   */
  getComplianceCases: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.org_id) {
        params.append('org_id', filters.org_id);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.unit_id) {
        params.append('unit_id', filters.unit_id);
      }
      const queryString = params.toString();
      const url = queryString ? `/compliance-cases/?${queryString}` : '/compliance-cases/';
      console.log('Fetching compliance cases from:', `${API_BASE_URL}${url}`);
      const response = await apiClient.get(url);
      console.log('Compliance cases response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance cases:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        url: error.config?.url
      });
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to fetch compliance cases');
    }
  },

  /**
   * Get compliance case detail with audit trail and quotes
   * @param {number} caseId - Compliance case ID
   * @returns {Promise<Object>} Full case detail
   */
  getComplianceCase: async (caseId) => {
    try {
      const response = await apiClient.get(`/compliance-cases/${caseId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching compliance case:', error.response?.status);
      throw new Error('Failed to fetch compliance case');
    }
  },

  /**
   * Get audit trail for a compliance case
   * @param {number} caseId - Compliance case ID
   * @returns {Promise<Array>} Audit trail entries
   */
  getComplianceCaseAuditTrail: async (caseId) => {
    try {
      const response = await apiClient.get(`/compliance-cases/${caseId}/audit-trail/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching audit trail:', error.response?.status);
      throw new Error('Failed to fetch audit trail');
    }
  },

  /**
   * Accept a compliance quote
   * @param {number} caseId - Compliance case ID
   * @param {number} quoteId - Quote ID
   * @returns {Promise<Object>} Updated case
   */
  acceptComplianceQuote: async (caseId, quoteId) => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/accept-quote/`, {
        quote_id: quoteId
      });
      return response.data;
    } catch (error) {
      console.error('Error accepting quote:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to accept quote');
    }
  },

  /**
   * Decline a compliance quote
   * @param {number} caseId - Compliance case ID
   * @param {number} quoteId - Quote ID
   * @param {string} reason - Decline reason
   * @returns {Promise<Object>} Updated case
   */
  declineComplianceQuote: async (caseId, quoteId, reason = '') => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/decline-quote/`, {
        quote_id: quoteId,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error declining quote:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to decline quote');
    }
  },

  approveInternalQuote: async (caseId) => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/approve-internal-quote/`);
      return response.data;
    } catch (error) {
      console.error('Error approving internal quote:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to approve quote');
    }
  },

  rejectInternalQuote: async (caseId, reason = '') => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/reject-internal-quote/`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error rejecting internal quote:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to reject quote');
    }
  },

  createManualQuote: async (caseId, lineItems) => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/create-manual-quote/`, { line_items: lineItems });
      return response.data;
    } catch (error) {
      console.error('Error creating manual quote:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to create manual quote');
    }
  },

  getCasePricingSchedule: async (caseId) => {
    try {
      const response = await apiClient.get(`/compliance-cases/${caseId}/pricing-schedule/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching pricing schedule:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || 'Failed to fetch pricing schedule');
    }
  },

  /**
   * Upload survey purchase order
   * @param {number} caseId - Compliance case ID
   * @param {File} file - PO document
   * @returns {Promise<Object>} Updated case
   */
  uploadSurveyPO: async (caseId, file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await apiClient.post(
        `/compliance-cases/${caseId}/upload-survey-po/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading survey PO:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to upload survey PO');
    }
  },

  /**
   * Upload remedial purchase order
   * @param {number} caseId - Compliance case ID
   * @param {File} file - PO document
   * @returns {Promise<Object>} Updated case
   */
  uploadRemedialPO: async (caseId, file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await apiClient.post(
        `/compliance-cases/${caseId}/upload-remedial-po/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading remedial PO:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to upload remedial PO');
    }
  },

  /**
   * Upload completion evidence
   * @param {number} caseId - Compliance case ID
   * @param {File} file - Evidence document
   * @param {string} description - Evidence description
   * @returns {Promise<Object>} Updated case
   */
  uploadCompletionEvidence: async (caseId, file, description = '') => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      if (description) {
        formData.append('description', description);
      }
      const response = await apiClient.post(
        `/compliance-cases/${caseId}/upload-completion-evidence/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading completion evidence:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to upload completion evidence');
    }
  },

  /**
   * Upload survey notes (Phase 6)
   * @param {number} unitId - Unit ID
   * @param {File} file - Survey notes DOCX file
   * @returns {Promise<Object>} Updated unit with tabs
   */
  uploadSurveyNotes: async (unitId, file) => {
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await apiClient.post(
        `/units/${unitId}/survey-notes/`,
        formData
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading survey notes:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.error || error.response?.data?.detail || 'Failed to upload survey notes');
    }
  },

  /**
   * Close a compliance case
   * @param {number} caseId - Compliance case ID
   * @returns {Promise<Object>} Updated case
   */
  closeComplianceCase: async (caseId) => {
    try {
      const response = await apiClient.post(`/compliance-cases/${caseId}/close/`);
      return response.data;
    } catch (error) {
      console.error('Error closing case:', error.response?.status, error.response?.data);
      throw new Error(error.response?.data?.detail || 'Failed to close case');
    }
  },

  /**
   * Get tenant summary PDF
   * @param {number} caseId - Compliance case ID
   * @returns {Promise<Blob>} PDF file blob
   */
  getComplianceTenantSummary: async (caseId) => {
    try {
      const response = await apiClient.get(`/compliance-cases/${caseId}/tenant-summary/`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant summary:', error.response?.status);
      throw new Error('Failed to fetch tenant summary');
    }
  },

  /**
   * Submit a bug report. `payload` is a plain object — if it includes a File for
   * `screenshot`, this method sends multipart; otherwise JSON.
   */
  submitBugReport: async (payload) => {
    const hasFile = payload && payload.screenshot instanceof Blob;
    try {
      if (hasFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formData.append(key, value);
        });
        const response = await apiClient.post('/bug-reports/', formData);
        return response.data;
      }
      const response = await apiClient.post('/bug-reports/', payload);
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail
        || (typeof error.response?.data === 'object' && error.response?.data
          ? Object.values(error.response.data).flat().join(' ')
          : null)
        || 'Failed to submit bug report';
      throw new Error(detail);
    }
  },

  /**
   * List bug reports for the superuser triage inbox. `params` may contain
   * `status`, `severity`, `category`, `search`, `ordering`, `page`, `page_size`.
   */
  listBugReports: async (params = {}) => {
    try {
      const response = await apiClient.get('/bug-reports/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching bug reports:', error.response?.status);
      throw new Error('Failed to fetch bug reports');
    }
  },

  /** Update a bug report (status, internal_notes, severity, category). */
  updateBugReport: async (bugId, patch) => {
    try {
      const response = await apiClient.patch(`/bug-reports/${bugId}/`, patch);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update bug report');
    }
  },

  /** Delete a bug report. */
  deleteBugReport: async (bugId) => {
    try {
      await apiClient.delete(`/bug-reports/${bugId}/`);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to delete bug report');
    }
  },

  /** Superuser-only: count of `new` bug reports for the header badge. */
  getBugReportUnreadCount: async () => {
    try {
      const response = await apiClient.get('/bug-reports/unread-count/');
      return response.data?.count ?? 0;
    } catch (error) {
      // 403 is expected for non-superusers; return 0 silently
      if (error.response?.status === 403) return 0;
      console.error('Error fetching bug-report unread count:', error.response?.status);
      return 0;
    }
  },

  /** Global header search across sites/units/devices/alerts. */
  globalSearch: async (query, limit = 5) => {
    try {
      const response = await apiClient.get('/search/', { params: { q: query, limit } });
      return response.data;
    } catch (error) {
      console.error('Error fetching search results:', error.response?.status);
      return { query, sites: [], units: [], devices: [], alerts: [] };
    }
  },

  // Expose the raw axios client for direct API calls
  api: apiClient,
};

export default authService;